import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getIdeas, checkAndMarkExpiredIdeas } from '../services/ideas';
import { getLivePrices, extractTickerFromUrl, isKalshiConfigured } from '../services/kalshi';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import toast from 'react-hot-toast';

const IdeasPage = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [filteredIdeas, setFilteredIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [filters, setFilters] = useState({
    platform: 'all',
    riskLevel: 'all',
    expirationDateRange: 'all',
    status: 'all',
  });
  const [livePrices, setLivePrices] = useState({});
  const [pricesLoading, setPricesLoading] = useState(false);

  // Fetch ideas on component mount
  useEffect(() => {
    fetchIdeas();
  }, []);

  // Apply filters and sorting when ideas, filters, or sortBy changes
  useEffect(() => {
    applyFiltersAndSort();
  }, [ideas, filters, sortBy]);

  // Fetch live prices for Kalshi ideas
  useEffect(() => {
    fetchLivePrices();
    
    // Refresh prices every 30 seconds
    const interval = setInterval(() => {
      fetchLivePrices();
    }, 30000);

    return () => clearInterval(interval);
  }, [ideas]);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      
      // Check and mark expired ideas as settled
      await checkAndMarkExpiredIdeas();
      
      const fetchedIdeas = await getIdeas();
      
      // Filter for pending users - only show ideas 14+ days old and active
      if (userProfile?.onboardingStatus === 'pending') {
        const fourteenDaysAgo = new Date();
        fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
        
        const filtered = fetchedIdeas.filter(idea => {
          const createdAt = idea.createdAt?.toDate ? idea.createdAt.toDate() : new Date(idea.createdAt);
          return createdAt <= fourteenDaysAgo && idea.status === 'active';
        });
        
        setIdeas(filtered);
      } else {
        // Approved users see all active ideas (not pending moderation)
        const filtered = fetchedIdeas.filter(idea => idea.status === 'active' || idea.status === 'resolved' || idea.status === 'settled' || idea.status === 'cancelled');
        setIdeas(filtered);
      }
    } catch (error) {
      console.error('Error fetching ideas:', error);
      toast.error('Failed to load ideas. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchLivePrices = async () => {
    if (!isKalshiConfigured()) {
      return; // Skip if Kalshi API is not configured
    }

    try {
      setPricesLoading(true);
      
      // Get all Kalshi ideas that are active
      const kalshiIdeas = ideas.filter(
        idea => idea.platform === 'Kalshi' && idea.status === 'active' && idea.marketUrl
      );

      if (kalshiIdeas.length === 0) {
        return;
      }

      // Extract tickers from market URLs
      const tickers = kalshiIdeas
        .map(idea => extractTickerFromUrl(idea.marketUrl))
        .filter(Boolean);

      if (tickers.length === 0) {
        return;
      }

      // Fetch live prices
      const prices = await getLivePrices(tickers);
      setLivePrices(prices);
    } catch (error) {
      console.error('Error fetching live prices:', error);
      // Don't show error toast - fail silently for live prices
    } finally {
      setPricesLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...ideas];

    // Apply filters
    if (filters.platform !== 'all') {
      filtered = filtered.filter(idea => idea.platform === filters.platform);
    }

    if (filters.riskLevel !== 'all') {
      filtered = filtered.filter(idea => idea.riskLevel === filters.riskLevel);
    }

    if (filters.expirationDateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(idea => {
        if (!idea.marketExpirationDate) return false;
        
        const expirationDate = new Date(idea.marketExpirationDate);
        
        switch (filters.expirationDateRange) {
          case 'thisWeek':
            const weekFromNow = new Date(today);
            weekFromNow.setDate(today.getDate() + 7);
            return expirationDate <= weekFromNow;
          case 'thisMonth':
            const monthFromNow = new Date(today);
            monthFromNow.setMonth(today.getMonth() + 1);
            return expirationDate <= monthFromNow;
          case 'next3Months':
            const threeMonthsFromNow = new Date(today);
            threeMonthsFromNow.setMonth(today.getMonth() + 3);
            return expirationDate <= threeMonthsFromNow;
          case 'next6Months':
            const sixMonthsFromNow = new Date(today);
            sixMonthsFromNow.setMonth(today.getMonth() + 6);
            return expirationDate <= sixMonthsFromNow;
          case 'nextYear':
            const yearFromNow = new Date(today);
            yearFromNow.setFullYear(today.getFullYear() + 1);
            return expirationDate <= yearFromNow;
          case 'overYear':
            const yearFromNowForOver = new Date(today);
            yearFromNowForOver.setFullYear(today.getFullYear() + 1);
            return expirationDate > yearFromNowForOver;
          default:
            return true;
        }
      });
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(idea => idea.status === filters.status);
    }

    // Apply sorting
    switch (sortBy) {
      case 'recent':
        filtered.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
        break;
      case 'totalReturn':
        filtered.sort((a, b) => {
          const aReturn = calculatePotentialReturn(a.entryPrice, a.targetPrice);
          const bReturn = calculatePotentialReturn(b.entryPrice, b.targetPrice);
          return bReturn - aReturn;
        });
        break;
      case 'closingSoon':
        filtered.sort((a, b) => {
          const aExpiration = a.marketExpirationDate ? new Date(a.marketExpirationDate) : new Date('2099-12-31');
          const bExpiration = b.marketExpirationDate ? new Date(b.marketExpirationDate) : new Date('2099-12-31');
          return aExpiration - bExpiration; // Ascending order (soonest first)
        });
        break;
      case 'irr':
        filtered.sort((a, b) => {
          const aIrr = calculateIRR(a);
          const bIrr = calculateIRR(b);
          return bIrr - aIrr; // Descending order (highest IRR first)
        });
        break;
      case 'trending':
        filtered.sort((a, b) => (b.votes || 0) - (a.votes || 0));
        break;
      case 'mostViewed':
        filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
        break;
      default:
        break;
    }

    setFilteredIdeas(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      platform: 'all',
      riskLevel: 'all',
      expirationDateRange: 'all',
      status: 'all',
    });
    setSortBy('recent');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatExpirationDate = (expirationDate) => {
    if (!expirationDate) return 'N/A';
    const date = new Date(expirationDate);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `${diffDays} days`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks`;
    if (diffDays <= 365) return `${Math.ceil(diffDays / 30)} months`;
    return `${Math.ceil(diffDays / 365)} years`;
  };

  const calculatePotentialReturn = (entryPrice, targetPrice) => {
    if (!entryPrice || !targetPrice) return 0;
    return (((targetPrice - entryPrice) / entryPrice) * 100).toFixed(1);
  };

  const calculateIRR = (idea) => {
    if (!idea.marketExpirationDate || !idea.entryPrice || !idea.targetPrice) return 0;
    
    const expirationDate = new Date(idea.marketExpirationDate);
    const createdDate = idea.createdAt?.toDate ? idea.createdAt.toDate() : new Date(idea.createdAt);
    const now = new Date();
    
    // Use the later of creation date or now as the start date
    const startDate = createdDate > now ? createdDate : now;
    
    // Calculate days until expiration
    const daysToExpiration = Math.ceil((expirationDate - startDate) / (1000 * 60 * 60 * 24));
    
    if (daysToExpiration <= 0) return 0;
    
    // Calculate the return percentage
    const returnRate = (idea.targetPrice - idea.entryPrice) / idea.entryPrice;
    
    // Convert to annualized IRR
    const yearsToExpiration = daysToExpiration / 365;
    if (yearsToExpiration <= 0) return 0;
    
    const annualizedReturn = Math.pow(1 + returnRate, 1 / yearsToExpiration) - 1;
    return annualizedReturn * 100; // Convert to percentage
  };

  const getRiskLevelColor = (riskLevel) => {
    switch (riskLevel?.toLowerCase()) {
      case 'low':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-blue-100 text-blue-700';
      case 'resolved':
        return 'bg-green-100 text-green-700';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getLivePriceForIdea = (idea) => {
    if (idea.platform !== 'Kalshi' || !idea.marketUrl) {
      return null;
    }

    const ticker = extractTickerFromUrl(idea.marketUrl);
    if (!ticker) {
      return null;
    }

    return livePrices[ticker];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Investment Ideas</h1>
          <p className="text-xl text-primary-100">
            Explore prediction market opportunities shared by our community
          </p>
        </div>
      </div>

      {/* Pending User Banner */}
      {userProfile?.onboardingStatus === 'pending' && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-start gap-3">
              <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-semibold text-yellow-900 mb-1">Limited Access - Onboarding Required</h3>
                <p className="text-sm text-yellow-800">
                  You're viewing ideas from 14+ days ago. Submit your first idea to unlock full platform access and see all recent ideas!
                </p>
                <button
                  onClick={() => navigate('/submit-idea')}
                  className="mt-2 text-sm font-medium text-yellow-900 underline hover:text-yellow-700"
                >
                  Submit Your First Idea →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters and Sorting Section */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Filter & Sort</h2>
            <button
              onClick={resetFilters}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Reset All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Sort By */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="recent">Recent</option>
                <option value="totalReturn">Total Return</option>
                <option value="closingSoon">Closing Soon</option>
                <option value="irr">IRR</option>
                <option value="trending">Most Voted</option>
                <option value="mostViewed">Most Viewed</option>
              </select>
            </div>

            {/* Platform Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Platform
              </label>
              <select
                value={filters.platform}
                onChange={(e) => handleFilterChange('platform', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Platforms</option>
                <option value="Kalshi">Kalshi</option>
                <option value="Polymarket">Polymarket</option>
                <option value="Manifold">Manifold</option>
                <option value="PredictIt">PredictIt</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Risk Level Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Risk Level
              </label>
              <select
                value={filters.riskLevel}
                onChange={(e) => handleFilterChange('riskLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Risk Levels</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
              </select>
            </div>

            {/* Expiration Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiration Date
              </label>
              <select
                value={filters.expirationDateRange}
                onChange={(e) => handleFilterChange('expirationDateRange', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Timeframes</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="next3Months">Next 3 Months</option>
                <option value="next6Months">Next 6 Months</option>
                <option value="nextYear">Next Year</option>
                <option value="overYear">Over 1 Year</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="resolved">Resolved</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex justify-between items-center mb-6">
          <p className="text-gray-600">
            Showing <span className="font-semibold text-gray-900">{filteredIdeas.length}</span> of{' '}
            <span className="font-semibold text-gray-900">{ideas.length}</span> ideas
          </p>
          {currentUser && userProfile?.onboardingStatus === 'approved' && (
            <button
              onClick={() => navigate('/submit-idea')}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              + Submit New Idea
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredIdeas.length === 0 && ideas.length === 0 && (
          <div className="text-center py-20">
            <svg
              className="mx-auto h-24 w-24 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
              />
            </svg>
            <h3 className="mt-6 text-xl font-medium text-gray-900">No Ideas Yet</h3>
            <p className="mt-2 text-gray-500">
              Be the first to share a prediction market opportunity!
            </p>
            {currentUser && (
              <button
                onClick={() => navigate('/submit-idea')}
                className="mt-6 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
              >
                Submit Your First Idea
              </button>
            )}
          </div>
        )}

        {/* No Results from Filters */}
        {!loading && filteredIdeas.length === 0 && ideas.length > 0 && (
          <div className="text-center py-20">
            <svg
              className="mx-auto h-24 w-24 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3 className="mt-6 text-xl font-medium text-gray-900">No Ideas Match Your Filters</h3>
            <p className="mt-2 text-gray-500">
              Try adjusting your filters to see more results.
            </p>
            <button
              onClick={resetFilters}
              className="mt-6 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
            >
              Reset Filters
            </button>
          </div>
        )}

        {/* Ideas Grid */}
        {!loading && filteredIdeas.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredIdeas.map((idea) => (
              <div
                key={idea.id}
                className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-gray-100 overflow-hidden cursor-pointer"
                onClick={() => navigate(`/ideas/${idea.id}`)}
              >
                {/* Card Header */}
                <div className="p-6">
                  <div className="flex justify-between items-start mb-3">
                    <span className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                      {idea.platform}
                    </span>
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(idea.status)}`}>
                      {idea.status}
                    </span>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 hover:text-primary-600 transition-colors">
                    {idea.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {idea.description}
                  </p>

                  {/* Risk and Expiration */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded border ${getRiskLevelColor(idea.riskLevel)}`}>
                      {idea.riskLevel} Risk
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                      Expires: {formatExpirationDate(idea.marketExpirationDate)}
                    </span>
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {idea.position}
                    </span>
                  </div>

                  {/* Prices */}
                  <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Entry</p>
                      <p className="text-sm font-semibold text-gray-900">{idea.entryPrice}¢</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Target</p>
                      <p className="text-sm font-semibold text-green-600">{idea.targetPrice}¢</p>
                    </div>
                  </div>

                  {/* Live Price from Kalshi - Only show if we have valid live data */}
                  {idea.platform === 'Kalshi' && idea.status === 'active' && (() => {
                    const livePrice = getLivePriceForIdea(idea);
                    if (!livePrice || livePrice.error) return null;
                    
                    if (livePrice.isClosed) {
                      return (
                        <div className="mb-4 p-3 bg-gray-100 rounded-lg border border-gray-300">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Market Status</p>
                              <p className="text-sm font-semibold text-gray-700">Closed</p>
                            </div>
                            {livePrice.settlementPrice !== null && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Settlement</p>
                                <p className="text-sm font-semibold text-gray-900">{livePrice.settlementPrice}¢</p>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }
                    
                    const priceChange = livePrice.currentPrice !== null && idea.entryPrice
                      ? ((livePrice.currentPrice - idea.entryPrice) / idea.entryPrice * 100).toFixed(1)
                      : null;
                    
                    return (
                      <div className="mb-4 p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                            <p className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Live Price</p>
                          </div>
                          <p className="text-xs text-blue-500">
                            {new Date(livePrice.lastUpdated).toLocaleTimeString()}
                          </p>
                        </div>
                        <div className="flex items-end justify-between">
                          <div>
                            <p className="text-2xl font-bold text-blue-900">
                              {livePrice.currentPrice !== null ? `${livePrice.currentPrice.toFixed(1)}¢` : 'N/A'}
                            </p>
                            {livePrice.bid !== null && livePrice.ask !== null && (
                              <p className="text-xs text-blue-600 mt-1">
                                Bid: {livePrice.bid}¢ | Ask: {livePrice.ask}¢
                              </p>
                            )}
                          </div>
                          {priceChange !== null && (
                            <div className={`px-2 py-1 rounded ${
                              parseFloat(priceChange) >= 0 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              <p className="text-xs font-semibold">
                                {parseFloat(priceChange) >= 0 ? '+' : ''}{priceChange}%
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Potential Return */}
                  <div className="mb-4 p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg">
                    <p className="text-xs text-primary-600 font-medium mb-1">Potential Return</p>
                    <p className="text-2xl font-bold text-primary-700">
                      {calculatePotentialReturn(idea.entryPrice, idea.targetPrice)}%
                    </p>
                  </div>

                  {/* Tags */}
                  {idea.tags && idea.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {idea.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                      {idea.tags.length > 3 && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                          +{idea.tags.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                        </svg>
                        <span>{idea.votes || 0}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                        </svg>
                        <span>{idea.views || 0}</span>
                      </div>
                    </div>
                    <span className="text-xs">{formatDate(idea.createdAt)}</span>
                  </div>

                  {/* Author */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500">
                      By <span className="font-medium text-gray-700">{idea.authorName}</span>
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default IdeasPage;



